/* eslint-disable camelcase, no-param-reassign */
import React, { useState } from 'react';
import {
  Formik,
  Field,
} from 'formik';
import {
  useDispatch,
} from 'react-redux';
import {
  useBlocker,
  useNavigate,
} from 'react-router';
import i18n from 'i18next';

import {
  coreActions,
} from '@gymcloud/core';

import {
  decodeParam,
} from 'web-utils';

import {
  Video,
  Button,
  Author,
  TagList,
  Tooltip,
  TagField,
  TextField,
  EditorField,
  MarkdownRenderer,
} from 'web-components';
import {
  Flex,
  H100,
} from 'web-styled';
import {
  OpenedTooltip,
} from 'web-types';

import * as S from './styled';

interface MatchParams {
  back: string;
}

type Props = {
  isPro?: boolean,
  entity: any,
  readOnly?: boolean,
  resourceType: 'exercises' | 'workoutExercises' | 'personalExercises';
  openedTooltips?: OpenedTooltip[],
  onSubmit?: () => void
};

const FillEntity = ({
  isPro = false,
  entity,
  readOnly = false,
  resourceType,
  openedTooltips = [],
  onSubmit,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);

  // Block navigating elsewhere when data has been entered into the input
  const blocker = useBlocker(({ currentLocation, nextLocation }) => (
    shouldBlockNavigation && currentLocation.pathname !== nextLocation.pathname));

  if (blocker.state === 'blocked') {
    const ask = window.confirm('Are you sure you want to leave without saving?');

    if (ask) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }

  const handleSubmit = (values, actions) => {
    setShouldBlockNavigation(false);
    onSubmit?.();
    const onSuccess = () => {
      const params = decodeParam(window.location.search) as MatchParams;
      if (entity.exercise_id
              && (entity.workout_type === 'PersonalWorkout'
                  || entity.workout_type === 'WorkoutTemplate')) {
        dispatch(
          coreActions.requestUpdateEntity({
            id: entity.exercise_id,
            payload: values,
            resourceType: 'exercises',
          }),
        );
      }

      if (params.back) {
        navigate(params.back, {
          state: { entity },
        });
      } else if (resourceType === 'exercises') {
        navigate(`/exercises/${entity.id}/overview`);
      } else {
        navigate(-1);
      }
    };

    dispatch(
      coreActions.requestUpdateEntity({
        id: entity.id,
        payload: values,
        endNotify: true,
        resourceType,
        callbacks: {
          ...actions,
          onSuccess,
        },
      }),
    );
  };

  const hasTag = entity?.tag_list !== undefined;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: entity?.name || '',
        tag_list: hasTag ? entity?.tag_list.join(',') : undefined,
        video_id: entity?.video_id || '',
        video_url: entity?.video_url || '',
        description: entity?.description || '',
      }}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <S.Container
          onKeyPress={() => {
            setShouldBlockNavigation(true);
          }}
          spacing={30}
          onSubmit={(e: any) => {
            e.preventDefault();
            formikProps.handleSubmit(e);
          }}
        >
          <S.Header>
            <Flex align="center">
              {entity?.author_full_name && (
                <Author
                  fullName={entity?.author_full_name}
                />
              )}
            </Flex>
            <Button
              id="formSubmitButton"
              type="submit"
              style={{ visibility: 'hidden' }}
            />
          </S.Header>
          <S.Content>
            <Flex
              column
              flex="0 0 50%"
              align="center"
            >
              <Video
                {...(readOnly
                  ? {
                    id: entity.video_id,
                    src: entity.video_url,
                    readOnly: true,
                  } : {
                    id: formikProps.values.video_id,
                    src: formikProps.values.video_url,
                  }
                )}
                title={entity?.name}
                height="400px"
                resourceType={resourceType}
                openedTooltips={openedTooltips}
                onAssign={(video) => {
                  setShouldBlockNavigation(true);
                  const id = video.embed_url.indexOf('vimeo') !== -1 ? video.id : null;
                  formikProps.setFieldValue('video_id', id);
                  formikProps.setFieldValue('video_url', video.embed_url);
                }}
                onUploaded={(videos) => {
                  setShouldBlockNavigation(true);
                  formikProps.setFieldValue('video_id', videos[0].id);
                  formikProps.setFieldValue('video_url', '');
                }}
                onDelete={() => {
                  setShouldBlockNavigation(true);
                  formikProps.setFieldValue('video_id', '');
                  formikProps.setFieldValue('video_url', '');
                }}
              />
            </Flex>
            {readOnly ? (
              <Flex
                column
                spacing={30}
                flex="1 1 auto"
                padding="0 0 30px 0"
              >
                <H100 color="primary">
                  {entity.name}
                </H100>

                <MarkdownRenderer
                  source={entity?.description}
                  placeholder={isPro
                    ? i18n.t('click.to.add.description', { type: 'exercise' })
                    : null}
                />

                <TagList
                  tags={entity.tag_list}
                  resourceType={resourceType}
                />
              </Flex>
            ) : (
              <Flex
                column
                spacing={30}
                flex="1 1 auto"
                padding="0 0 30px 0"
              >
                <Field
                  flex="0 0 60px"
                  name="name"
                  label="Name"
                  color="primary"
                  component={TextField}
                />
                {openedTooltips?.some(el => el.key === 'enter_description') ? (
                  <Tooltip
                    body="Enter a written description here and Save when you are done."
                    toggleMode
                    initialState
                    positions={['top']}
                  >
                    <Flex width="100%">
                      <Field
                        name="description"
                        color="primary"
                        label="Description"
                        placeholder="Type exercise description here..."
                        component={EditorField}
                      />
                    </Flex>
                  </Tooltip>
                ) : (
                  <Field
                    name="description"
                    color="primary"
                    label="Description"
                    placeholder="Type exercise description here..."
                    component={EditorField}
                  />
                )}
                <Field
                  name="tag_list"
                  component={TagField}
                />
              </Flex>
            )}
          </S.Content>
        </S.Container>
      )}
    </Formik>
  );
};

export default FillEntity;
